'use client'

import { useState, useCallback } from 'react'
import dynamic from 'next/dynamic'
import { useTranslations } from 'next-intl'
import { Tag } from 'lucide-react'
import type { PropertyDetailData, ReviewStatsData } from '@/services/property-detail'
import type { PropertyDiningData } from '@/services/property-dining-types'
import { PropertyGallery } from './PropertyGallery'
import { PropertyDining } from './PropertyDining'
import { WhatsAppPropertyOverride } from '../../../components/whatsapp/WhatsAppPropertyOverride'
import { PropertyOverview } from './PropertyOverview'
import { PropertyAmenities } from './PropertyAmenities'
import { PropertyRoomTypes } from './PropertyRoomTypes'
import { PropertyLandmarks } from './PropertyLandmarks'
import { PropertyPolicies } from './PropertyPolicies'
import { BookingSidebar } from './BookingSidebar'
import { OnlineBookingDisabledNotice } from './OnlineBookingDisabledNotice'
import { CouponBanner } from '../../../components/CouponBanner'

// Lazy load below-fold heavy components
const PropertyMap = dynamic(() => import('./PropertyMap').then(m => ({ default: m.PropertyMap })))
const PropertyReviews = dynamic(() => import('./PropertyReviews').then(m => ({ default: m.PropertyReviews })))

export interface SessionData {
  sessionCode: string
  checkIn: string
  checkOut: string
  adults: number
  children: number
  couponCode?: string
  agencyDiscount?: { discountType: string; discountValue: number; agencyName?: string }
  guestDiscount?: { discountType: string; discountValue: number }
  /** Room-type IDs the active discount applies to (undefined = all room types) */
  allowedRoomTypeIds?: string[]
  /** Decision for the contextual banner — see evaluateCouponEligibility. */
  guestCouponEligibility?: import('@/services/coupon').CouponEligibility
  /** Full preview so the banner can render the discount value when eligible. */
  guestCouponPreview?: import('@/services/coupon').CouponPreview
}

interface PropertyDetailClientProps {
  property: PropertyDetailData
  reviewStats: ReviewStatsData | null
  dining: PropertyDiningData
  tenantId: string
  locale: string
  sessionData?: SessionData
  /** Promo code to prefill (no session): the coupon a tracked campaign link left in `tk_cc`. */
  initialCouponCode?: string
}

/**
 * ⛔ MONEY (NIZ-31): `totalRate` / `averageRate` are TAX-INCLUSIVE — the price
 * the guest is quoted and the amount that becomes the payment. `netTotal` +
 * `taxTotal` is the split behind it.
 */
export type AvailabilityMap = Record<string, {
  available: number
  totalRate: number
  averageRate: number
  netTotal: number
  taxTotal: number
  currency: string
}>

export function PropertyDetailClient({
  property,
  reviewStats,
  dining,
  tenantId,
  locale,
  sessionData,
  initialCouponCode,
}: PropertyDetailClientProps) {
  const tSearch = useTranslations('booking.search')
  const [availability, setAvailability] = useState<AvailabilityMap | null>(null)
  const [searchedDates, setSearchedDates] = useState<{
    checkIn: string
    checkOut: string
    adults: number
    children: number
    nights: number
    couponCode?: string
    discount?: { discountType: string; discountValue: number }
    /** Room types the discount applies to. Undefined = all room types. */
    allowedRoomTypeIds?: string[]
  } | null>(null)

  const handleAvailabilityLoaded = useCallback((
    avail: AvailabilityMap,
    dates: {
      checkIn: string
      checkOut: string
      adults: number
      children: number
      nights: number
      couponCode?: string
      discount?: { discountType: string; discountValue: number }
      allowedRoomTypeIds?: string[]
    },
  ) => {
    setAvailability(avail)
    setSearchedDates(dates)
    // Scroll to rooms section
    setTimeout(() => {
      document.getElementById('rooms')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 100)
  }, [])

  return (
    <div className="max-w-7xl mx-auto px-4 pt-0 pb-6 sm:py-6">
      {/* Point the floating WhatsApp button at THIS property */}
      <WhatsAppPropertyOverride
        whatsapp={property.socialLinks?.whatsapp ?? null}
        propertyName={locale === 'ar' && property.name_ar ? property.name_ar : property.name_en}
      />
      {/* Gallery */}
      <PropertyGallery images={property.images} propertyName={property.name_en} />

      {/* Content + Sidebar Layout */}
      <div className="mt-6 flex flex-col lg:flex-row gap-8">
        {/* Left: Scrollable content */}
        <div className="flex-1 min-w-0 space-y-10">
          {/* Overview */}
          <PropertyOverview
            property={property}
            reviewStats={reviewStats}
            locale={locale}
          />

          {/* Amenities */}
          {property.allAmenities.length > 0 && (
            <PropertyAmenities amenities={property.allAmenities} />
          )}

          {/* Landmarks */}
          {property.landmarks.length > 0 && (
            <PropertyLandmarks landmarks={property.landmarks} locale={locale} />
          )}

          {/* Discount banner — server-rendered eligibility wins (it knows the
              full coupon context); fall back to the local "applied" message
              when only `searchedDates.discount` is available (e.g. user just
              entered a code in the sidebar without a backing CouponPreview). */}
          {sessionData?.guestCouponEligibility ? (
            <CouponBanner
              eligibility={sessionData.guestCouponEligibility}
              preview={sessionData.guestCouponPreview}
            />
          ) : searchedDates?.discount && (
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3 flex items-center gap-2">
              <Tag className="h-4 w-4 text-green-600 dark:text-green-400 shrink-0" />
              <span className="text-sm text-green-700 dark:text-green-400">
                {searchedDates.discount.discountType === 'percentage'
                  ? <><strong>{tSearch('promoDiscountPercent', { percent: searchedDates.discount.discountValue })}</strong> &mdash; {tSearch('appliedToBooking')}</>
                  : <><strong>{tSearch('promoDiscountFixed', { amount: searchedDates.discount.discountValue, currency: 'SAR' })}</strong> &mdash; {tSearch('appliedAtCheckout')}</>
                }
              </span>
            </div>
          )}

          {/* Dining & buffets — showcased right above the rooms so the menus
              feed straight into the room picker. No prices by design. */}
          <PropertyDining dining={dining} locale={locale} />

          {/* Room Types */}
          <section id="rooms">
            <PropertyRoomTypes
              property={property}
              tenantId={tenantId}
              locale={locale}
              checkIn={searchedDates?.checkIn}
              checkOut={searchedDates?.checkOut}
              adults={searchedDates?.adults}
              // eslint-disable-next-line react/no-children-prop -- "children" means number of child guests, not React children
              children={searchedDates?.children}
              nights={searchedDates?.nights}
              couponCode={searchedDates?.couponCode}
              discount={searchedDates?.discount}
              allowedRoomTypeIds={searchedDates?.allowedRoomTypeIds ?? sessionData?.allowedRoomTypeIds}
              availability={availability || undefined}
              sessionCode={sessionData?.sessionCode}
            />
          </section>

          {/* Map */}
          {(property.googleMapUrl || (property.latitude && property.longitude)) && (
            <PropertyMap
              googleMapUrl={property.googleMapUrl}
              address={property.address}
              propertyName={property.name_en}
              latitude={property.latitude}
              longitude={property.longitude}
            />
          )}

          {/* Policies */}
          {property.policies && (
            <PropertyPolicies policies={property.policies as Record<string, { en?: string; ar?: string }>} locale={locale} />
          )}

          {/* Reviews */}
          {reviewStats && reviewStats.totalCount > 0 && (
            <PropertyReviews
              propertyId={property.id}
              reviewStats={reviewStats}
            />
          )}
        </div>

        {/* Right: Sticky booking sidebar (desktop) */}
        <div className="lg:w-[360px] shrink-0">
          {property.isBookingEnabled ? (
            <BookingSidebar
              property={property}
              tenantId={tenantId}
              onAvailabilityLoaded={handleAvailabilityLoaded}
              sessionData={sessionData}
              initialCouponCode={initialCouponCode}
            />
          ) : (
            <OnlineBookingDisabledNotice />
          )}
        </div>
      </div>

      {/* Bottom spacer for mobile sticky bar */}
      <div className="h-20 lg:hidden" />
    </div>
  )
}
